import React, { useCallback, useState } from "react";
import { get, isString, join, keys, toNumber, uniqueId } from "lodash";
import styled from "styled-components";
import { Color } from "@glossy/colors";
import Image from "@ui-kit/identity/image";
import account from "./placeholders/account.svg?raw";
import accounts from "./placeholders/accounts.svg?raw";
import admin from "./placeholders/admin.svg?raw";
import benchmark from "./placeholders/benchmark.svg?raw";
import corpus from "./placeholders/corpus.svg?raw";
import dashboard from "./placeholders/dashboard.svg?raw";
import focus_query from "./placeholders/focus_query.svg?raw";
import link from "./placeholders/link.svg?raw";
import milestone from "./placeholders/milestone.svg?raw";
import monitoring_alert from "./placeholders/monitoring_alert.svg?raw";
import place from "./placeholders/place.svg?raw";
import places from "./placeholders/places.svg?raw";
import project from "./placeholders/project.svg?raw";
import reader from "./placeholders/reader.svg?raw";
import social_alert from "./placeholders/social_alert.svg?raw";
import source_query from "./placeholders/source_query.svg?raw";
import superadmin from "./placeholders/superadmin.svg?raw";
import user from "./placeholders/user.svg?raw";
import user_outline from "./placeholders/user_outline.svg?raw";
import writer from "./placeholders/writer.svg?raw";

const BORDER_WIDTH = 2;
const PADDING = 3;
const BORDER_OFFSET = PADDING + BORDER_WIDTH / 2;
const EXTRA_WIDTH = (BORDER_WIDTH + PADDING) * 2;

// Predefined image sizes
const SIZES = {
  disk: { s: 24, m: 32, l: 48 },
  square: { s: 28, m: 32, l: 48 },
};

enum Shape {
  Disk = "disk",
  Square = "square",
}

// Types of objects
const TYPES = {
  account: { shape: Shape.Disk, placeholder: account },
  accounts: { shape: Shape.Disk, placeholder: accounts },
  benchmark: { shape: Shape.Disk, placeholder: benchmark },
  corpus: { shape: Shape.Disk, placeholder: corpus },
  dashboard: { shape: "square", placeholder: dashboard },
  user: { shape: Shape.Disk, placeholder: user },
  user_outline: { shape: Shape.Disk, placeholder: user_outline },
  reader: { shape: Shape.Disk, placeholder: reader },
  writer: { shape: Shape.Disk, placeholder: writer },
  admin: { shape: Shape.Disk, placeholder: admin },
  superadmin: { shape: Shape.Disk, placeholder: superadmin },
  focus_query: { shape: Shape.Disk, placeholder: focus_query },
  source_query: { shape: Shape.Disk, placeholder: source_query },
  link: { shape: "square", placeholder: link },
  milestone: { shape: "square", placeholder: milestone },
  monitoring_alert: { shape: Shape.Disk, placeholder: monitoring_alert },
  social_alert: { shape: Shape.Disk, placeholder: social_alert },
  place: { shape: Shape.Disk, placeholder: place },
  places: { shape: Shape.Disk, placeholder: places },
  project: { shape: "square", placeholder: project },
};
export type Type = keyof typeof TYPES;
export const TYPE_NAMES = keys(TYPES);

// SVGs for state icons
const STATES = {
  error: (
    <g>
      <circle r="7" fill={Color.Ruby} />
      <circle cy="4" r="1" fill="#FFF" />
      <line
        y1="-4"
        y2="1"
        strokeWidth="2"
        stroke="#FFF"
        strokeLinecap="round"
      />
    </g>
  ),
  warning: (
    <g>
      <circle r="7" fill={Color.DarkTangerine} />
      <circle cy="4" r="1" fill="#FFF" />
      <line
        y1="-4"
        y2="1"
        strokeWidth="2"
        stroke="#FFF"
        strokeLinecap="round"
      />
    </g>
  ),
  info: (
    <g>
      <circle r="7" fill="#0BA0DB" />
      <circle cy="-4" r="1" fill="#FFF" />
      <line
        y1="4"
        y2="-1"
        strokeWidth="2"
        stroke="#FFF"
        strokeLinecap="round"
      />
    </g>
  ),
  paused: (
    <g>
      <circle r="7" fill={Color.DarkTangerine} />
      <line
        x1="-2"
        y1="-2"
        x2="-2"
        y2="2"
        stroke="#FFF"
        strokeLinecap="round"
        strokeWidth="2"
      />
      <line
        x1="2"
        y1="-2"
        x2="2"
        y2="2"
        stroke="#FFF"
        strokeLinecap="round"
        strokeWidth="2"
      />
    </g>
  ),
  blocked: (
    <g>
      <circle r="7" fill={Color.Ruby} />
      <line
        x1="-3"
        y1="-3"
        x2="3"
        y2="3"
        stroke="#FFF"
        strokeLinecap="round"
        strokeWidth="2"
      />
      <line
        x1="3"
        y1="-3"
        x2="-3"
        y2="3"
        stroke="#FFF"
        strokeLinecap="round"
        strokeWidth="2"
      />
    </g>
  ),
  warmup: (
    <g>
      <circle r="7" fill="#1FAA7A" />
      <path
        d="M-4,0a4,4,0,1,0,4,-4"
        stroke="#FFF"
        fill="none"
        strokeLinecap="round"
        strokeWidth="2"
      />
      <polyline
        points="-5.93 0.62 -4.17 -1.81 -1.74 -0.05"
        stroke="#FFF"
        fill="none"
        strokeLinecap="round"
        strokeWidth="2"
      />
    </g>
  ),
  locked: (
    <g>
      <circle r="7" fill="#C91D42" />
      <path
        d="M-2,-1v-2a2,2,0,0,1,2,-2h0a2,2,0,0,1,2,2v2"
        fill="none"
        stroke="#FFF"
        strokeWidth="2"
      />
      <rect x="-4" y="-1" width="8" height="6" rx="1" ry="1" fill="#FFF" />
    </g>
  ),
};
export type State = keyof typeof STATES;
export const STATE_NAMES = keys(STATES);

interface IProps {
  // Radius of inner disk (image). One of "s" (24), "m" (32), "l" (48), or a custom number of pixels
  size?: "s" | "m" | "l" | number;
  // Type of represented object. Can be "person", "people" or "query".
  type?: Type;
  // URL of picture
  picture: string;
  // State shown as a little round colored icon in bottom right corner
  state?: State;
  // Color for the image border!
  borderColor?: string;
  // Applies grayscale filter to image
  grayscale?: boolean;
  // Draws a dotted inverted border
  editable?: boolean;
  // Additionnal classes for styling
  className?: string;
  // Action to do when clicked
  onClick?: () => void;
  // Behaviour when picture state changes
  onLoadedChange?: (loaded: boolean, error: boolean) => void;
  // Display shadow
  displayShadow?: boolean;
}

// Returns shape given type
export const getShape = (type: Type): Shape =>
  get(TYPES, [type, "shape"]) as Shape;

// Returns the side size of the image
export const getImageSize = (type: Type, size: number): number =>
  get(SIZES, [get(TYPES, [type, "shape"], Shape.Disk), size], toNumber(size));

// Returns border radius
const getBorderRadius = (imageSize: number): number =>
  imageSize / 2 + PADDING + BORDER_WIDTH / 2;

// Computes final size of the component
export const getCanvasSize = (imageSize: number, hasBorder: boolean): number =>
  imageSize + (hasBorder ? EXTRA_WIDTH : 0);

// Computes viewBox, taking the border into account
export const getViewBox = (imageSize: number, hasBorder: boolean): string => {
  const extraWidth = hasBorder ? EXTRA_WIDTH / 2 : 0;
  return join(
    [
      0 - extraWidth,
      0 - extraWidth,
      imageSize + extraWidth * 2,
      imageSize + extraWidth * 2,
    ],
    " "
  );
};

const getStateCenter = (
  imageShape: Shape,
  imageSize: number,
  hasBorder: boolean,
  iconSize: number
): { x: number; y: number } => {
  if (imageShape === Shape.Disk) {
    // disk
    const _radius = (imageSize + (hasBorder ? PADDING : 0)) / 2;
    const _offset = _radius + Math.sqrt(Math.pow(_radius, 2) / 2);
    return { x: _offset + 1, y: _offset };
  }
  // square
  const _offset = imageSize - iconSize / 2 + 1;
  return { x: _offset, y: _offset + 1 };
};

const Root = styled.svg<{ editable: boolean }>`
  overflow: visible;
  display: inline-block;
  vertical-align: middle;
  ${({ editable }) =>
    editable &&
    `
  transition: transform .3s;
  :hover {
    transform: scale(1.1);
    cursor: pointer;
  }
`}
`;

const ImageMask = ({
  id,
  shape,
  size,
}: {
  id: string;
  shape: Shape;
  size: number;
}) => (
  <mask id={id}>
    {/* Keep the white disk part only */}
    {shape === Shape.Disk ? (
      <circle cx={size / 2} cy={size / 2} r={size / 2} fill="#FFF" />
    ) : (
      <rect
        width={size}
        height={size}
        rx={size / 8}
        ry={size / 8}
        fill="#FFF"
      />
    )}
  </mask>
);

const StateIconMask = ({
  id,
  center: { x, y },
  radius,
}: {
  id: string;
  center: any;
  radius: number;
}) => (
  <mask id={id}>
    {/* Keep everything in this white rectangle… */}
    <rect x="-100%" y="-100%" width="300%" height="300%" fill="#FFF" />
    {/* …and cuts out this black circle */}
    <circle cx={x} cy={y} r={radius} fill="#000" />
  </mask>
);

const DropShadow = ({ id }: { id: string }) => (
  <filter id={id}>
    <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
    <feOffset dx="0" dy="1" result="offsetblur" />
    <feFlood floodOpacity=".2" />
    <feComposite in2="offsetblur" operator="in" />
    <feMerge>
      <feMergeNode />
      <feMergeNode in="SourceGraphic" />
    </feMerge>
  </filter>
);

const GrayScale = ({ id }) => (
  <filter id={id}>
    <feColorMatrix type="saturate" values="0" />
  </filter>
);

const Border = ({
  shape,
  size,
  radius,
  color = "#FFF",
  strokeDasharray,
}: {
  shape: Shape;
  size: number;
  radius: number;
  color?: string;
  strokeDasharray?: string;
}) =>
  shape === Shape.Disk ? (
    <circle
      cx={size / 2}
      cy={size / 2}
      r={radius}
      fill="none"
      strokeWidth={BORDER_WIDTH}
      stroke={color}
      strokeDasharray={strokeDasharray}
    />
  ) : (
    <rect
      x={-BORDER_OFFSET}
      y={-BORDER_OFFSET}
      width={size + BORDER_OFFSET * 2}
      height={size + BORDER_OFFSET * 2}
      rx={size / 8 + BORDER_OFFSET}
      ry={size / 8 + BORDER_OFFSET}
      fill="none"
      strokeWidth={BORDER_WIDTH}
      stroke={color}
      strokeDasharray={strokeDasharray}
    />
  );

const StateIcon = ({
  center: { x, y },
  size,
  state,
}: {
  center: { x: number; y: number };
  size: number;
  state: State;
}) => {
  const translate = join([x, y], " ");
  const scale = size === 14 ? 1 : 10 / 14;
  return (
    <g transform={`translate(${translate}), scale(${scale})`}>
      {STATES[state]}
    </g>
  );
};

const Background = ({ size }: { size: number }) => (
  <rect x="0" y="0" width={size} height={size} fill="#ddd" />
);

const Placeholder = ({
  hasBorder,
  size,
  type,
}: {
  hasBorder: boolean;
  size: number;
  type: Type;
}) => {
  const transform = hasBorder
    ? `scale(${size / (size + EXTRA_WIDTH)})`
    : undefined;
  const innerHTML = get(TYPES, [type, "placeholder"]);
  return (
    <g transform={transform} dangerouslySetInnerHTML={{ __html: innerHTML }} />
  );
};

const useImageStatus = (onLoadedChange) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const loadedChange = useCallback(
    (loaded: boolean, error: boolean): void => {
      setLoaded(loaded);
      setError(error);
      onLoadedChange && onLoadedChange(loaded, error);
    },
    [onLoadedChange]
  );

  return { loaded, error, loadedChange };
};

const UiKitIdentityPortrait = ({
  size = "m",
  type = "account",
  picture,
  state,
  borderColor,
  editable,
  className,
  onClick,
  grayscale,
  onLoadedChange,
  displayShadow,
}: IProps) => {
  const _size = isString(size) ? { s: 24, m: 32, l: 48 }[size] : size;
  const hasBorder = !!borderColor || !!editable;
  const hasState = !!state;
  const shape = getShape(type);
  const hasShadow = shape === Shape.Square && displayShadow;
  const imageSize = getImageSize(type, _size);
  const iconSize = size < 18 ? 10 : 14;
  const canvasSize = getCanvasSize(imageSize, hasBorder);
  const borderRadius = getBorderRadius(imageSize);
  const portraitId = uniqueId("portrait_");
  const imageMaskId = `${portraitId}_image`;
  const stateMaskId = `${portraitId}_state`;
  const shadowId = `${portraitId}_shadow`;
  const grayscaleId = `${portraitId}_grayscale`;
  const stateCenter = getStateCenter(shape, imageSize, hasBorder, iconSize);
  const _picture = picture;
  const strokeDasharray = editable ? "2 2" : undefined;
  const filter = hasShadow ? `url(#${shadowId})` : undefined;
  const { loaded, error, loadedChange } = useImageStatus(onLoadedChange);
  const displayPlaceholder = !loaded || !_picture || error;

  return (
    <Root
      viewBox={getViewBox(imageSize, hasBorder)}
      width={canvasSize}
      height={canvasSize}
      className={className}
      editable={!!editable}
      onClick={onClick}
    >
      <defs>
        <ImageMask id={imageMaskId} shape={shape} size={imageSize} />
        {hasState && (
          <StateIconMask
            id={stateMaskId}
            center={stateCenter}
            radius={iconSize / 2 + 2}
          />
        )}
        {hasShadow && <DropShadow id={shadowId} />}
        {grayscale && <GrayScale id={grayscaleId} />}
      </defs>
      <g style={{ filter }}>
        <g mask={state ? `url(#${stateMaskId})` : undefined}>
          {hasBorder && (
            <Border
              shape={shape}
              size={imageSize}
              radius={borderRadius}
              color={borderColor}
              strokeDasharray={strokeDasharray}
            />
          )}
          <g mask={`url(#${imageMaskId})`}>
            <Background size={imageSize} />
            {displayPlaceholder && (
              <Placeholder hasBorder={hasBorder} size={imageSize} type={type} />
            )}
            <g filter={grayscale ? `url(#${grayscaleId})` : undefined}>
              <Image
                url={_picture}
                size={imageSize}
                onLoadedChange={loadedChange}
              />
            </g>
          </g>
        </g>
      </g>
      {hasState && (
        <StateIcon center={stateCenter} size={iconSize} state={state} />
      )}
    </Root>
  );
};

export default UiKitIdentityPortrait;
